/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Map, CheckSquare, FileText, AppWindow,
  BarChart3, Newspaper, Settings, Users, Palette, BookOpen,
  Rocket, Lock, ArrowLeft, ChevronDown
} from 'lucide-react';

const SIDEBAR_JOURNEY = [
  { icon: LayoutDashboard, label: 'Dashboard', active: true },
  { icon: Map, label: 'My Journey' },
  { icon: BookOpen, label: 'Programme Details' },
];

const SIDEBAR_SPRINGBOARD = [
  { icon: CheckSquare, label: 'Tasks' },
  { icon: FileText, label: 'Documents' },
  { icon: AppWindow, label: 'Business Apps' },
  { icon: BarChart3, label: 'Business Dashboard' },
  { icon: Users, label: 'Team' },
  { icon: Palette, label: 'Branding' },
];

const SIDEBAR_OTHER = [
  { icon: Newspaper, label: 'News' },
  { icon: Settings, label: 'Settings' },
];

const STAGES = [
  { num: 1, name: 'Assessment', status: 'complete' },
  { num: 2, name: 'Idea Generation', status: 'current' },
  { num: 3, name: 'Validation', status: 'locked' },
  { num: 4, name: 'Business Setup', status: 'locked' },
  { num: 5, name: 'Asset Foundation', status: 'locked' },
  { num: 6, name: 'Fundraising', status: 'locked' },
  { num: 7, name: 'Go to Market', status: 'locked' },
  { num: 8, name: 'Advisory & Structure', status: 'locked' },
  { num: 9, name: 'Scaling', status: 'locked' },
];

export default function PortalPreview() {
  const navigate = useNavigate();

  return (
    <div style={{ minHeight: '100vh', display: 'flex', position: 'relative', background: '#fafafa' }} data-testid="portal-preview">
      {/* Locked overlay */}
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0,
        zIndex: 100, background: '#1E491A', padding: '10px 24px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Lock size={16} color="#F6ADBC" />
          <span style={{ color: '#fff', fontSize: 14 }}>
            Preview mode — this is what the SPRING platform looks like once you are in.
          </span>
        </div>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: '#F6ADBC', color: '#1E491A', border: 'none',
            padding: '6px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6, borderRadius: 0,
          }}
          data-testid="exit-preview-btn"
        >
          <ArrowLeft size={14} /> Back to your results
        </button>
      </div>

      {/* Sidebar */}
      <aside style={{
        width: 240, background: '#fff', borderRight: '1px solid #e5e5e5',
        position: 'fixed', height: '100%', paddingTop: 48, overflow: 'auto',
        opacity: 0.7, pointerEvents: 'none',
      }}>
        {/* Logo */}
        <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid #eee' }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1E491A', fontFamily: "var(--font)", letterSpacing: -1.5, margin: 0 }}>
            spring<span style={{ display: 'inline-block', width: 7, height: 7, background: '#F6ADBC', borderRadius: '50%', marginLeft: 1, verticalAlign: 'baseline', position: 'relative', top: -2 }} />
          </h1>
        </div>

        {/* User */}
        <div style={{ padding: 16, borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 36, height: 36, background: '#F6ADBC', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14 }}>Y</div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Your Name</div>
            <div style={{ fontSize: 12, color: '#999' }}>Setting up...</div>
          </div>
        </div>

        {/* Nav sections */}
        <nav style={{ padding: '16px 12px' }}>
          <div style={{ padding: '0 16px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, color: '#999', marginBottom: 8 }}>Journey</div>
          {SIDEBAR_JOURNEY.map((item, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', marginBottom: 2,
              background: item.active ? '#1E491A' : 'transparent', color: item.active ? '#fff' : '#666',
              fontSize: 14,
            }}>
              <item.icon size={16} /><span>{item.label}</span>
            </div>
          ))}

          <div style={{ borderTop: '1px solid #eee', margin: '16px 0' }} />

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px' }}>
            <Rocket size={16} /><span style={{ fontSize: 14, fontWeight: 700, flex: 1 }}>Springboard</span>
            <ChevronDown size={14} />
          </div>
          <div style={{ marginLeft: 16, paddingLeft: 12, borderLeft: '2px solid #F6ADBC44' }}>
            {SIDEBAR_SPRINGBOARD.map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 16px', color: '#8B7000', fontSize: 14 }}>
                <item.icon size={16} /><span>{item.label}</span>
              </div>
            ))}
          </div>

          <div style={{ borderTop: '1px solid #eee', margin: '16px 0' }} />

          {SIDEBAR_OTHER.map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', color: '#666', fontSize: 14 }}>
              <item.icon size={16} /><span>{item.label}</span>
            </div>
          ))}
        </nav>
      </aside>

      {/* Main content area */}
      <main style={{ flex: 1, marginLeft: 240, paddingTop: 48 }}>
        <div style={{ padding: 32, pointerEvents: 'none', opacity: 0.65 }}>
          {/* Dashboard header */}
          <div style={{ marginBottom: 32 }}>
            <h1 style={{ fontSize: 28, fontWeight: 800, color: '#1a1a1a', margin: '0 0 4px' }}>Welcome to SPRING</h1>
            <p style={{ fontSize: 15, color: '#888', margin: 0 }}>Your guided journey to building your next income.</p>
          </div>

          {/* Stats cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 32 }}>
            <div style={{ background: '#fff', border: '1px solid #eee', padding: 20 }}>
              <div style={{ fontSize: 13, color: '#888', marginBottom: 4 }}>Current Stage</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#1E491A' }}>2 — Idea Generation</div>
            </div>
            <div style={{ background: '#fff', border: '1px solid #eee', padding: 20 }}>
              <div style={{ fontSize: 13, color: '#888', marginBottom: 4 }}>Progress</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#1E491A' }}>11%</div>
              <div style={{ height: 6, background: '#eee', marginTop: 8 }}>
                <div style={{ height: 6, background: '#F6ADBC', width: '11%' }} />
              </div>
            </div>
            <div style={{ background: '#fff', border: '1px solid #eee', padding: 20 }}>
              <div style={{ fontSize: 13, color: '#888', marginBottom: 4 }}>Tasks</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#1E491A' }}>0 of 3</div>
            </div>
          </div>

          {/* Stage journey */}
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1a1a1a', marginBottom: 16 }}>Your Journey</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {STAGES.map(s => (
              <div key={s.num} style={{
                display: 'flex', alignItems: 'center', gap: 16, padding: '14px 16px',
                background: s.status === 'current' ? 'rgba(19,21,59,0.06)' : '#f8f8f8',
                border: s.status === 'current' ? '2px solid #1E491A' : '1px solid #eee',
                opacity: s.status === 'locked' ? 0.4 : 1,
              }}>
                <div style={{
                  width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: s.status === 'complete' ? '#2D6A4F' : s.status === 'current' ? '#1E491A' : '#ccc',
                  color: '#fff', fontSize: 13, fontWeight: 700,
                }}>
                  {s.status === 'complete' ? '✓' : s.num}
                </div>
                <span style={{ flex: 1, fontSize: 15, fontWeight: s.status === 'current' ? 600 : 400, color: '#1a1a1a' }}>{s.name}</span>
                <span style={{ fontSize: 13, color: '#999' }}>
                  {s.status === 'complete' ? 'Complete' : s.status === 'current' ? 'In Progress' : 'Locked'}
                </span>
                {s.status === 'locked' && <Lock size={14} color="#ccc" />}
              </div>
            ))}
          </div>

          {/* Quick links */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginTop: 32 }}>
            <div style={{ background: '#fff', border: '1px solid #eee', padding: 20 }}>
              <CheckSquare size={20} color="#1E491A" style={{ marginBottom: 8 }} />
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>Tasks</div>
              <div style={{ fontSize: 13, color: '#888' }}>Your stage tasks and progress</div>
            </div>
            <div style={{ background: '#fff', border: '1px solid #eee', padding: 20 }}>
              <FileText size={20} color="#1E491A" style={{ marginBottom: 8 }} />
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>Documents</div>
              <div style={{ fontSize: 13, color: '#888' }}>Templates, uploads, and reports</div>
            </div>
            <div style={{ background: '#fff', border: '1px solid #eee', padding: 20 }}>
              <BarChart3 size={20} color="#1E491A" style={{ marginBottom: 8 }} />
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>Business Dashboard</div>
              <div style={{ fontSize: 13, color: '#888' }}>Track your business metrics</div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
